import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import HeaderBar from '../HeaderBar/HeaderBar'
import styles from './ManageSubscription.module.css'
import axios from 'axios'
import CancelSubscriptionModal from '../UsageHistory/CancelSubscriptionModal'
import PopUp from '../Popup/Popup'
import { API_BASE_URL, getAgentById, getEndUserSubscriptions_Billings, refundAndCancelSubscriptionAgnetApi, getReactivationStatus } from '../../Store/apiStore'
import { useAgentStore } from '../../Store/agentDetailStore'
import decodeToken from '../../lib/decodeToken'
import { isEmbeddedCheckoutEnabled } from '../../lib/embeddedCheckoutFlag'
import { useCheckoutStore } from '../../Store/checkoutStore'
import { clearAgentPaid } from '../../lib/paymentGuard'
import { setPaymentCancelReturn } from '../PaymentCancel/PaymentCancel'

const API_BASE = process.env.REACT_APP_API_BASE_URL

// "Oct 15, 2026" style — with year — for the "active until" line.
const fmtFull = (d) => {
  if (!d) return ''
  const dt = new Date(d)
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Plans shown in the "Upgrade your plan" section. Starter is DELIBERATELY
// excluded — this section only ever offers Scaler + Growth (the upgrade tiers).
// Same product NAMEs / icons as RecommendedPlan's PLAN_DEFS so the two screens
// describe the same plans identically.
const PLAN_DEFS = {
  starter: { order: 0, icon: '/svg/starter-icon_new.svg' },
  scaler: { order: 1, icon: '/svg/scaler-icon_new.svg', recommended: true },
  growth: { order: 2, icon: '/svg/growth-icon_icon.svg' },
}

// Per-plan brand colour for the "Current Plan" subtitle — SAME hues as the big
// plan name in the upgrade cards (.cardBottom h3): Scaler orange, Growth blue,
// Starter/PAYG/free stay green. Used on both this page and the Usage tab so the
// plan-name colour is consistent everywhere.
const PLAN_COLORS = {
  scaler: '#FE5E00',
  growth: '#00A4FF',
  starter: '#E83BAC',
}
const planColor = (name) => PLAN_COLORS[String(name || '').trim().toLowerCase()] || '#00AD6D'

// Light circle-background behind the Current Plan icon — SAME tints as the
// upgrade-card icons (.planIcon.scaler / .growth): Scaler light-orange, Growth
// light-blue, Starter light-pink. PAYG / free stay the green default.
const PLAN_ICON_BG = {
  scaler: '#fff8f4',
  growth: '#e7f7ff',
  starter: '#ffeef8',
}
const planIconBg = (name) => PLAN_ICON_BG[String(name || '').trim().toLowerCase()] || '#ddf8ee'

// Matches every PAYG label — "PAYG", "Pay-As-You-Go", and "PAYG Extra" (the
// actual agentPlan value). Using startsWith/includes rather than an exact list
// so "PAYG Extra" isn't misread as an SMB plan (which made Manage Subscription
// show the $0.48 per-minute rate instead of the $5 monthly fee).
const isPaygName = (name) => {
  const n = String(name || '').trim().toLowerCase()
  return n.startsWith('payg') || n.includes('pay-as-you-go') || n.includes('pay as you go')
}

const PlanIcon = ({ type }) => (
  <span className={`${styles.planIcon} ${styles[type]}`} aria-hidden="true">
    <img src={PLAN_DEFS[type]?.icon || '/svg/scaler-icon_new.svg'} alt="" />
  </span>
)

const ConsultationIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M6.66642 0C5.77184 0 5.03467.704388 5.03467 1.55919v3.17058c0 .8548.73717 1.55919 1.63175 1.55919h.91435l.97605 1.1949c.10678.1315.2705.20784.44452.20784.17322 0 .33774-.07634.44451-.20784l.97605-1.1949h.9128c.8946 0 1.6325-.70439 1.6325-1.55919V1.55919C12.9672.704388 12.2285 0 11.3347 0H6.66642Zm0 1.07549h4.66748c.2911 0 .5063.20632.5063.4837v3.17058c0 .27737-.216.4837-.5063.4837h-1.188l.0008.00076c-.17164.00075-.33378.07633-.43978.20557l-.70633.86235-.70555-.86235c-.10599-.12924-.26813-.20557-.44057-.20557H6.66644c-.29107 0-.50621-.20633-.50621-.4837V1.55994c0-.27737.21593-.4837.50621-.4837l-.00002-.00075ZM4.19143 6.45659c-1.41819 0-2.58332 1.10723-2.58332 2.46011 0 .69154.30452 1.3166.79095 1.764-1.416616.6402-2.39906 2.0149-2.39906 3.6059v1.1753l.000791-.0015C0 15.7574.250741 15.9992.561578 16h7.255492c.1495.0007.29345-.0559.40024-.1572.10599-.1013.16531-.2388.16452-.3824v-1.1753c0-1.5902-.98159-2.9641-2.39744-3.6044.48722-.4474.79016-1.0732.79016-1.7647 0-1.35286-1.16351-2.46011-2.58251-2.46011h-.00061Zm9.61807 0c-1.4182 0-2.5833 1.10723-2.5833 2.46011 0 .69154.3045 1.3166.791 1.764-1.4166.6402-2.39909 2.0149-2.39909 3.6059v1.1753l.00079-.0015c-.00079.1428.05853.2796.16373.3809.1052.1013.24837.1587.39787.1587h7.2587c.3109-.0008.5616-.2426.5608-.5396v-1.1753c0-1.5909-.9808-2.9657-2.3974-3.6059.4864-.4474.7909-1.0725.7909-1.764 0-1.35286-1.1651-2.46011-2.5833-2.46011h-.0007ZM4.19143 7.53207c.81311 0 1.45695.61597 1.45695 1.38459 0 .7694-.64384 1.38084-1.45695 1.38084-.8131 0-1.45853-.61146-1.45853-1.38084 0-.76939.64464-1.38459 1.45853-1.38459Zm9.61807 0c.8131 0 1.4586.61597 1.4586 1.38459 0 .7694-.6447 1.38084-1.4586 1.38084-.8131 0-1.4585-.61146-1.4585-1.38084 0-.76939.6446-1.38459 1.4585-1.38459ZM4.19143 11.3736c1.70927 0 3.06504 1.2909 3.06504 2.9129v.6386H1.12578v-.6386c0-1.6212 1.3563-2.9129 3.06565-2.9129Zm9.61807 0c1.7093 0 3.0651 1.2909 3.0651 2.9129v.6386h-6.1293v-.6386c0-1.6212 1.3557-2.9129 3.065-2.9129h-.0008Z" fill="#6327E9" />
  </svg>
)

// One upgrade card. `locked` = the user already has this plan. Blur/lock styling
// is copied EXACTLY from RecommendedPlan's current-plan card: inline
// { opacity: 0.55, cursor: 'not-allowed' } + a "Current Plan" badge with the
// same inline colors — so the two screens dim the current plan identically.
const UpgradeCard = ({ type, name, price, minutes, recommended, locked, isCurrentPlan, isDowngrade, loading, onUpgrade }) => (
  <article
    className={`${styles.upgradeCard} ${styles[type]}`}
    style={locked ? { opacity: 0.55, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
    aria-disabled={locked || undefined}
  >
    {recommended && <span className={styles.recommended}>Recommended</span>}
    {/* Same two badges RecommendedPlan uses: "Current Plan" for your plan,
        "Lower Tier" for a downgrade (e.g. Scaler when you're on Growth). */}
    {isCurrentPlan && (
      <span style={{ position: 'absolute', top: 8, right: 12, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#eef2ff', color: '#4f46e5', zIndex: 2 }}>
        Current Plan
      </span>
    )}
    {!isCurrentPlan && isDowngrade && (
      <span style={{ position: 'absolute', top: 8, right: 12, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#f3f4f6', color: '#6b7280', zIndex: 2 }}>
        Lower Tier
      </span>
    )}
    <div className={styles.cardTop}>
      <div className={styles.priceGroup}>
        <PlanIcon type={type} />
        <div>
          <strong>${price != null ? Number(price).toLocaleString() : '—'}/month</strong>
          <small>Per Receptionist</small>
        </div>
      </div>
      <p className={styles.minutes}><b>{minutes != null ? Number(minutes).toLocaleString() : '—'}</b><span>minutes</span></p>
    </div>
    <div className={styles.cardBottom}>
      <h3>{name}</h3>
      <button type="button" onClick={onUpgrade} disabled={locked || loading}>
        {/* Button text by tier (blur/lock stays handled by `locked` above):
            current plan → "Current Plan", a lower tier → "Not downgraded",
            a higher tier → "Upgrade plan". */}
        {loading
          ? 'Starting…'
          : isCurrentPlan
            ? 'Current Plan'
            : isDowngrade
              ? 'Not downgraded'
              : 'Upgrade plan'}
      </button>
    </div>
  </article>
)

// Grey shimmer placeholder card shown while /products loads — same idea as
// RecommendedPlan's skeletonCard, so the user never sees an empty gap.
const SkeletonCard = () => (
  <article className={`${styles.upgradeCard} ${styles.skeletonCard}`} aria-hidden="true">
    <div className={styles.cardTop}>
      <div className={styles.priceGroup}>
        <span className={`${styles.skel} ${styles.skelIcon}`} />
        <div>
          <span className={`${styles.skel} ${styles.skelPrice}`} />
          <span className={`${styles.skel} ${styles.skelSub}`} />
        </div>
      </div>
      <span className={`${styles.skel} ${styles.skelMinutes}`} />
    </div>
    <div className={styles.cardBottom}>
      <span className={`${styles.skel} ${styles.skelName}`} />
      <span className={`${styles.skel} ${styles.skelBtn}`} />
    </div>
  </article>
)

const ManageSubscription = () => {
  // 'month' | 'year' — same shape as RecommendedPlan's billingInterval so the
  // price shown and the price we send to checkout stay in sync.
  const [billingInterval, setBillingInterval] = useState('month')

  // Agent id from the More-menu / See-Plan navigation (state), sessionStorage fallback.
  const location = useLocation()
  const agentId = location?.state?.agentId || sessionStorage.getItem('SelectAgentId')

  // Shared agent cache — the SAME store the More-menu writes on cancel, so an
  // immediate cancel here reflects everywhere (dashboard, agent card) identically.
  const getCachedAgent = useAgentStore((s) => s.getAgentById)
  const setCachedAgent = useAgentStore((s) => s.setAgentById)

  const [agent, setAgent] = useState(null)
  const [activeUntil, setActiveUntil] = useState('') // "Plan active until" — same as Usage page

  // Current Plan card — dynamic, mirrors the Usage & Billing page exactly.
  const [currentPlanName, setCurrentPlanName] = useState('') // agentPlan
  const [currentFee, setCurrentFee] = useState(null)          // PAYG monthly_fee / SMB plan_unit_amount
  const [isPayg, setIsPayg] = useState(false)

  // Upgrade cards — fetched from the SAME /products feed RecommendedPlan uses.
  const [products, setProducts] = useState([])

  // "You're on the highest plan" popup — shown when every upgrade tier is the
  // user's current plan or below (same trigger idea as RecommendedPlan).
  // topTierAlertedRef is a LATCH so the popup fires only ONCE per page visit —
  // without it, the effect below re-fires every time `cards` recomputes (on a
  // Monthly/Yearly toggle, cancel-modal open, etc.) and the popup reappears.
  const [showTopTier, setShowTopTier] = useState(false)
  const topTierAlertedRef = useRef(false)

  // Checkout — direct hit of /create-checkout-session on card tap (no detour
  // through RecommendedPlan). checkoutLoadingId = the card currently starting
  // checkout; checkoutError surfaces a failure/409 message.
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null)
  const [checkoutError, setCheckoutError] = useState('')

  // Cancel Subscription — SAME refund-aware flow as the More-menu, shown in the
  // SAME <PopUp> success/failure dialog (checkmark icon + Close button).
  const [showCancel, setShowCancel] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [popupType, setPopupType] = useState('success') // 'success' | 'failed'

  // Already-cancelled lock — SAME signal the More-menu uses (FastAgentDetails).
  const [cancelScheduledUntil, setCancelScheduledUntil] = useState(null)
  const isCancelScheduled = !!cancelScheduledUntil

  // Fully-ended detection — reactivation mode 'checkout' means the plan is
  // canceled/refunded. Needed for PAYG, where cancel leaves agentPlan as "PAYG
  // Extra" in the DB (only Stripe knows it's canceled), so agentPlan alone can't
  // tell ended from active. Same source UsageHistory uses.
  const [reactivation, setReactivation] = useState(null)
  const [reactivationLoaded, setReactivationLoaded] = useState(false) // status resolved?
  // Does the agent's CURRENT subscription read as active? (PAYG row status / SMB
  // sub status). The agent's subscriptionId is repointed on reactivation, so
  // this is the truth about "live plan now" — it overrides a stale "ended" from
  // the reactivation API (which can resolve an old cancelled sub).
  const [hasActiveSub, setHasActiveSub] = useState(false)
  // Ended ONLY when the plan being reactivated is the agent's CURRENT plan (or
  // the current plan is free/blank). Guards the "cancel old → buy new" case:
  // the reactivation API can resolve a stale canceled sub, which would wrongly
  // flag ended over a live plan (e.g. cancel PAYG → buy Growth shows Growth).
  const _curKey = String(currentPlanName || '').trim().toLowerCase()
  const _reactKey = String(reactivation?.planName || '').trim().toLowerCase()
  const _curIsFree = _curKey === '' || _curKey === 'free'
  const isEndedPlan =
    reactivation?.canReactivate &&
    reactivation?.mode === 'checkout' &&
    (_curIsFree || _curKey === _reactKey) &&
    // If the agent's CURRENT subscription is active (cancel → reactivate created
    // a new live sub the reactivation API didn't resolve), it is NOT ended. Same
    // fix as the Usage & Billing tab, so both pages agree.
    !hasActiveSub

  // Re-fetch trigger — bump after a cancel so the lock appears immediately.
  const [refresh, setRefresh] = useState(0)

  // ── Load agent + current plan/fee + active-until (mirror of Usage page) ──
  useEffect(() => {
    if (!agentId) return
    let alive = true
    setReactivationLoaded(false) // re-gate the plan header until status resolves
    setHasActiveSub(false)       // re-evaluate active status on each load
    ;(async () => {
      try {
        const agentRes = await getAgentById(agentId)
        if (!alive) return
        const ag = agentRes?.data || null
        setAgent(ag)

        const agentPlan = ag?.agentPlan || ''
        setCurrentPlanName(agentPlan)
        const payg = isPaygName(agentPlan)
        setIsPayg(payg)

        // Reactivation status — tells us if the plan is fully ended (canceled/
        // refunded) regardless of the stale DB agentPlan. Independent fetch.
        getReactivationStatus(agentId)
          .then((s) => { if (alive) setReactivation(s || null) })
          .catch(() => { if (alive) setReactivation(null) })
          .finally(() => { if (alive) setReactivationLoaded(true) })

        if (payg) {
          // PAYG → $5 monthly_fee + access_until from /agent-payg-details.
          const paygData = await axios
            .post(`${API_BASE_URL}/agent-payg-details`, { agent_id: agentId })
            .then((r) => r.data?.data || null)
            .catch(() => null)
          if (!alive) return
          setCurrentFee(paygData?.monthly_fee != null ? Number(paygData.monthly_fee) : null)
          setActiveUntil(fmtFull(paygData?.access_until))
          setHasActiveSub(String(paygData?.status || '').trim().toLowerCase() === 'active')
        } else if (ag?.userId) {
          // SMB → plan_unit_amount + current_period_end from latestSubscription.
          const subs = await getEndUserSubscriptions_Billings(ag.userId).catch(() => null)
          const myAgent = (subs?.agents || []).find((a) => a.agent_id === agentId)
          const sub = myAgent?.latestSubscription || null
          if (!alive) return
          setCurrentFee(sub?.plan_unit_amount != null ? Number(sub.plan_unit_amount) : null)
          setActiveUntil(fmtFull(sub?.current_period_end || sub?.next_renewal_date))
          setHasActiveSub(['active', 'trialing'].includes(String(sub?.status || '').trim().toLowerCase()))

          // Scheduled-cancel lock — same detection as FastAgentDetails.
          const scheduled =
            String(sub?.status || '').trim().toLowerCase() === 'cancel_scheduled' ||
            sub?.payment_status === 'payment_canceled_on_end'
          if (scheduled) {
            setCancelScheduledUntil(fmtFull(sub?.next_renewal_date || sub?.current_period_end))
          }
        }
      } catch (e) {
        console.error('[ManageSubscription] load failed:', e?.message)
      }
    })()
    return () => { alive = false }
  }, [agentId, refresh])

  // ── Load plans from the SAME /products feed RecommendedPlan reads ──
  // Filtered to Scaler + Growth (never Starter), sorted by tier order.
  useEffect(() => {
    let cancelled = false
    const authToken = localStorage.getItem('token') || ''
    fetch(`${API_BASE}/products`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return
        const enriched = data
          .filter((p) => (p.name || '').trim().toLowerCase() in PLAN_DEFS)
          .sort((a, b) => {
            const ak = (a.name || '').trim().toLowerCase()
            const bk = (b.name || '').trim().toLowerCase()
            return PLAN_DEFS[ak].order - PLAN_DEFS[bk].order
          })
        setProducts(enriched)
      })
      .catch(() => { /* leave cards empty on failure */ })
    return () => { cancelled = true }
  }, [])

  // Price/minutes helpers — identical math to RecommendedPlan.getDisplayPrice.
  const priceForInterval = (product, interval) =>
    product?.prices?.find((p) => p.interval === interval)

  const displayPrice = (product) => {
    const monthly = priceForInterval(product, 'month')
    const yearly = priceForInterval(product, 'year')
    if (billingInterval === 'year' && yearly) return yearly.unit_amount / 100 / 12
    if (monthly) return monthly.unit_amount / 100
    return null
  }

  const displayMinutes = (product) => {
    // The /products feed carries the raw Stripe product list under `data`, and
    // the real metadata (with minutes-month) lives there — the top-level
    // product.metadata and per-price metadata are NOT reliable objects here.
    // So match the raw product by id and read its metadata, exactly like
    // RecommendedPlan does. Fall back to the other shapes just in case.
    const matchedRaw = product?.data?.find?.((p) => p.id === product.id) || null
    const monthly = priceForInterval(product, 'month')
    return (
      matchedRaw?.metadata?.['minutes-month'] ||
      product?.metadata?.['minutes-month'] ||
      monthly?.metadata?.['minutes-month'] ||
      null
    )
  }

  // Which upgrade tier is the user's CURRENT plan → that card gets blurred/locked.
  const currentPlanKey = String(currentPlanName || '').trim().toLowerCase()

  // Tier ladder for downgrade detection. Includes Starter (0) even though it's
  // never rendered here — it's only needed to know a plan's RANK so that, on
  // Growth, the lower Scaler card is locked too. Same order as RecommendedPlan.
  const TIER_ORDER = { starter: 0, scaler: 1, growth: 2 }
  const currentPlanOrder = Number.isFinite(TIER_ORDER[currentPlanKey]) ? TIER_ORDER[currentPlanKey] : -1

  const cards = useMemo(
    () =>
      products.map((product) => {
        const key = (product.name || '').trim().toLowerCase()
        const def = PLAN_DEFS[key] || {}
        const cardOrder = Number.isFinite(TIER_ORDER[key]) ? TIER_ORDER[key] : -1
        // Lock the current plan AND any lower tier — identical rule to
        // RecommendedPlan (isCurrentPlan || isDowngrade). This is why on Growth
        // BOTH cards blur: Growth = current, Scaler = lower tier (downgrade).
        const isCurrentPlan = key === currentPlanKey
        const isDowngrade =
          currentPlanOrder >= 0 && cardOrder >= 0 && cardOrder < currentPlanOrder
        return {
          id: key,
          name: product.name,
          recommended: !!def.recommended,
          price: displayPrice(product),
          minutes: displayMinutes(product),
          raw: product,
          isCurrentPlan,
          isDowngrade,
          locked: isCurrentPlan || isDowngrade,
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, billingInterval, currentPlanKey],
  )

  // Every upgrade tier locked (user is on Growth, the top) → "highest plan".
  // Latched via topTierAlertedRef so it shows ONCE — re-computing `cards` on a
  // toggle or modal open won't re-open it after the user dismissed it.
  useEffect(() => {
    if (cards.length === 0) return
    if (topTierAlertedRef.current) return
    if (cards.every((c) => c.locked)) {
      topTierAlertedRef.current = true
      setShowTopTier(true)
    }
  }, [cards])

  // Upgrade → hit checkout DIRECTLY (no RecommendedPlan detour). This is the
  // SAME request RecommendedPlan.handleContinue makes: identical body
  // (customerId / priceId / userId / url / cancelUrl / currency / agentId /
  // isUpgrade / oldSubscriptionId), the same /create-checkout-session endpoint,
  // and the same embedded-sheet-vs-hosted-redirect branching on the response.
  const handleUpgrade = async (card) => {
    if (!card || card.locked || checkoutLoadingId) return

    // A click here IS a deliberate upgrade — same as FastAgentDetails'
    // handleUpgradeClick, which clears the "just paid" duplicate-payment guard
    // BEFORE routing to checkout so a legitimate new purchase isn't blocked.
    // (RecommendedPlan's Continue then passes its own isAgentRecentlyPaid check
    // because this clear already ran.) Without this, a recent test payment would
    // bounce the user to /fast-agent-detail instead of opening checkout.
    clearAgentPaid(agentId)

    const a = agent
    const token = localStorage.getItem('token') || ''
    const decoded = decodeToken(token) || {}
    const customerId = decoded?.customerId
    // Owner-aware userId (team members act on the owner's behalf), same order
    // handleContinue uses. Backend re-resolves the real owner from the agent.
    const selectedOwnerId = sessionStorage.getItem('SelectAgentOwnerId')
    const teamOwnerId = localStorage.getItem('teamOwnerId')
    const userId = selectedOwnerId || teamOwnerId || decoded?.id

    if (!token || !customerId || !userId) {
      setCheckoutError('Missing account info. Please refresh and sign in again.')
      return
    }

    // The tapped plan's price id for the interval the toggle is on.
    const priceObj = card.raw?.prices?.find((p) => p.interval === billingInterval)
    if (!priceObj?.id) {
      setCheckoutError(`${card.name} isn't available for ${billingInterval === 'year' ? 'yearly' : 'monthly'} billing right now.`)
      return
    }

    // Old sub id so the backend can retire the superseded plan after checkout.
    // Same source RecommendedPlan uses: FastAgentDetails stores the agent's
    // current subscriptionId under 'upgradeOldSubId'. We prefer that exact key,
    // and keep agent.subscriptionId as the fallback (it's the same value — the
    // sessionStorage entry is set FROM agent.subscriptionId). Only a HINT to the
    // backend anyway: cancelSupersededSubscription resolves the real old sub by
    // listing the customer's Stripe subs and classifying them by product.
    const oldSubscriptionId =
      sessionStorage.getItem('upgradeOldSubId') ||
      a?.subscriptionId ||
      a?.subscription?.id ||
      undefined

    // Success/cancel — this is an upgrade flow, so mirror handleContinue's
    // upgrade path: land on /go-live-setup with the upgrade markers.
    const successParams = new URLSearchParams({ fromCheckout: '1' })
    if (agentId) successParams.append('agentId', agentId)
    if (userId) successParams.append('userId', userId)
    successParams.append('upgraded', '1')
    const successUrl = `${window.location.origin}/go-live-setup?${successParams.toString()}`
    const cancelUrl = `${window.location.origin}/cancel-payment`

    // Return here if the user cancels the Stripe sheet.
    const cancelReturn = window.location.pathname + (window.location.search || '?from=upgrade')
    sessionStorage.setItem('cancelReturnTo', cancelReturn)
    setPaymentCancelReturn(cancelReturn)
    sessionStorage.setItem('SelectAgentId', agentId || a?.agent_id || '')
    // Keep the sessionStorage contract identical to the FastAgentDetails →
    // RecommendedPlan path, so the post-checkout bookkeeping reads the same keys
    // whether the upgrade started there or here.
    if (oldSubscriptionId) sessionStorage.setItem('upgradeOldSubId', oldSubscriptionId)
    sessionStorage.setItem('currentAgentPlan', a?.agentPlan || currentPlanName || '')

    const embedded = isEmbeddedCheckoutEnabled()

    try {
      setCheckoutLoadingId(card.id)
      setCheckoutError('')
      const res = await axios.post(
        `${API_BASE}${embedded ? '/create-checkout-session-embedded' : '/create-checkout-session'}`,
        {
          customerId,
          priceId: priceObj.id,
          userId,
          url: successUrl,
          cancelUrl,
          currency: (priceObj.currency || 'usd').toLowerCase(),
          agentId,
          isUpgrade: true, // REQUIRED — a real upgrade must pass the 409 dup guard
          oldSubscriptionId,
          clientFiresPurchase: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )

      if (embedded && res?.data?.clientSecret) {
        // Same in-page CheckoutSheet RecommendedPlan opens.
        useCheckoutStore.getState().openCheckout({
          clientSecret: res.data.clientSecret,
          sessionId: res.data.sessionId,
          hostedUrl: res.data.checkoutUrl || null,
          meta: {
            agentId,
            userId,
            planName: card.name,
            next: '/go-live-setup',
            extraParams: { upgraded: '1' },
          },
        })
        setCheckoutLoadingId(null)
        return
      }

      if (res?.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl
        return
      }
      setCheckoutError('Could not start checkout. Please try again.')
    } catch (err) {
      console.error('[ManageSubscription] checkout error:', err?.response?.data || err?.message)
      const apiMessage = err?.response?.status === 409 ? err?.response?.data?.message : null
      setCheckoutError(apiMessage || 'Could not start checkout. Please try again.')
    } finally {
      setCheckoutLoadingId(null)
    }
  }

  const handleCancelConfirmed = async () => {
    if (!agentId || isCancelScheduled) return
    const minsLeft = agent?.mins_left ? Math.floor(agent.mins_left / 60) : 0
    setCancelLoading(true)
    try {
      const result = await refundAndCancelSubscriptionAgnetApi(agentId, minsLeft)
      setShowCancel(false)
      setPopupType('success')
      setMessage(result?.message || 'Subscription cancellation processed.')

      // IDENTICAL to the More-menu cancel: only paint the agent as dead when the
      // backend actually tore it down (refund-eligible → immediate).
      const wasImmediate = result?.immediate === true || result?.eligible === true
      const cached = getCachedAgent(agentId)
      if (wasImmediate && cached?.agentData) {
        setCachedAgent(
          agentId,
          {
            ...cached.agentData,
            agent: { ...cached.agentData.agent, agentPlan: 'free', isDeactivated: 1, mins_left: 0 },
          },
          cached.assignedNumbers,
          cached.totalBookings,
        )
      }
      setRefresh((p) => p + 1)
    } catch (error) {
      setShowCancel(false)
      setPopupType('failed')
      setMessage(`Failed to cancel subscription: ${error?.message || 'Unknown error'}`)
    } finally {
      setCancelLoading(false)
    }
  }

  // No active plan: agentPlan reverts to "free"/blank (SMB), OR the plan is
  // fully ended/refunded (PAYG keeps "PAYG Extra" in the DB, so we rely on the
  // reactivation 'checkout' signal). Drives the free/ended treatment: "free"
  // label, hidden fee, hidden cancel footer — same as Usage & Billing.
  const isFreePlan = currentPlanKey === 'free' || currentPlanKey === '' || isEndedPlan

  // Don't decide the plan header until the reactivation status has resolved —
  // otherwise a cancelled PAYG briefly shows the active "PAYG / $5 / cancel"
  // layout before flipping to "free" (the confusing flash). While not ready, the
  // label is blank and the fee/note/footer stay hidden.
  const planReady = reactivationLoaded

  // Current Plan display bits. When ended, show "free" (not "PAYG Extra").
  const currentPlanLabel = !planReady
    ? ''
    : (isFreePlan ? 'free' : (isPayg ? 'PAYG - pay as you go' : (currentPlanName || '—')))
  const currentIcon = isPayg ? '/svg/payg_icon.svg' : (PLAN_DEFS[currentPlanKey]?.icon || '/svg/payg_icon.svg')
  const feeLabel = currentFee != null
    ? `$${Number.isInteger(currentFee) ? currentFee : Number(currentFee).toFixed(2)}`
    : '—'

  return (
    <main className={styles.page}>
      {/* Plain block wrapper so the shared HeaderBar fills the width and stays
          left-aligned (like Usage & Billing). Without it, being a direct child
          of the flex-column .page let the >600px `margin:0 auto` rule center the
          back-arrow + title. */}
      <div className={styles.headerWrap}>
        <HeaderBar title="Manage Subscription" />
      </div>
      <div className={styles.content}>
        <section className={styles.currentPlan}>
          <div className={styles.currentTop}>
            <div className={styles.currentTitle}>
              <span
                className={styles.currentIcon}
                aria-hidden="true"
                style={{
                  background: !planReady
                    // Loading → neutral grey circle (NOT green), same as the
                    // Usage tab's loading state, so the two pages match.
                    ? '#ececf0'
                    : (!isFreePlan && !isPayg ? planIconBg(currentPlanKey) : '#ddf8ee'),
                }}
              >
                {/* Only render the icon once the plan is resolved — otherwise it
                    shows a default (payg leaf) that then visibly changes to the
                    real plan icon (e.g. Growth), which confuses the user. Empty
                    circle while loading. */}
                {planReady && <img src={currentIcon} alt="" />}
              </span>
              <div><h2>Current Plan</h2><p style={{ color: (planReady && !isFreePlan && !isPayg) ? planColor(currentPlanKey) : '#00AD6D' }}>{currentPlanLabel}</p></div>
            </div>
            {/* Monthly Plan Fee — only once the plan state is resolved and there
                IS an active plan. Avoids the flash + the free/ended case. */}
            {planReady && !isFreePlan && (
              <div className={styles.fee}><span>Monthly Plan Fee</span><b>{feeLabel}</b></div>
            )}
          </div>
          {/* Usage note — PAYG only, active only, after the state resolves. */}
          {planReady && isPayg && !isFreePlan && (
            <p className={styles.usageNote}><span> <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_5893_512)">
                <path d="M11 5.54091V6.00091C10.9994 7.07912 10.6503 8.12825 10.0047 8.99182C9.35908 9.85539 8.45164 10.4871 7.41768 10.7929C6.38372 11.0986 5.27863 11.0619 4.26724 10.6882C3.25584 10.3145 2.39233 9.62396 1.80548 8.71944C1.21863 7.81492 0.939896 6.74494 1.01084 5.66907C1.08178 4.59319 1.4986 3.56907 2.19914 2.74944C2.89968 1.92982 3.84639 1.3586 4.89809 1.12098C5.9498 0.883364 7.05013 0.992077 8.035 1.43091M11 2.00091L6 7.00591L4.5 5.50591" stroke="#00AD6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_5893_512">
                  <rect width="12" height="12" fill="white" />
                </clipPath>
              </defs>
            </svg></span> Usage charges are billed separately.</p>
          )}
        </section>

        <section className={styles.billingCycle} aria-label="Billing cycle">
          <div><strong>Billing cycle</strong><small>Annual (Save up to 17%)</small></div>
          <div className={styles.cycleControl}>
            <span className={billingInterval === 'month' ? styles.cycleActive : ''}>Monthly</span>
            <button
              type="button"
              className={`${styles.switch} ${billingInterval === 'year' ? styles.switchOn : ''}`}
              aria-label="Toggle monthly or yearly billing"
              aria-pressed={billingInterval === 'year'}
              onClick={() => setBillingInterval((v) => (v === 'month' ? 'year' : 'month'))}
            ><i /></button>
            <span className={billingInterval === 'year' ? styles.cycleActive : ''}>Yearly</span>
          </div>
        </section>

        <div className={styles.divider} />

        <section>
          <div className={styles.upgradeHeading}>
            <h2>Upgrade your plan</h2>
            <button
              type="button"
              className={styles.consultation}
              onClick={() => window.open('https://www.rxpt.ai/contact-us', '_blank', 'noopener,noreferrer')}
            >
              <ConsultationIcon /> Book free consultation
            </button>
          </div>
          <div className={styles.upgrades}>
            {cards.length === 0 ? (
              // Skeleton cards (Scaler + Growth) while /products loads.
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : (
              cards.map((card) => (
                <UpgradeCard
                  key={card.id}
                  type={card.id}
                  name={String(card.name || '').toUpperCase()}
                  price={card.price}
                  minutes={card.minutes}
                  recommended={card.recommended}
                  locked={card.locked}
                  isCurrentPlan={card.isCurrentPlan}
                  isDowngrade={card.isDowngrade}
                  loading={checkoutLoadingId === card.id}
                  onUpgrade={() => handleUpgrade(card)}
                />
              ))
            )}
          </div>
          {checkoutError && <p className={styles.checkoutError}>{checkoutError}</p>}
        </section>

        {/* Cancel footer only once the plan state is resolved AND there's an
            active plan to cancel. A fully ended/refunded plan (free) has nothing
            to cancel and no "active until" date, so hide the whole footer. The
            planReady gate stops it flashing for a cancelled PAYG while loading. */}
        {planReady && !isFreePlan && (
          <footer className={styles.footer}>
            <button
              type="button"
              onClick={() => setShowCancel(true)}
              disabled={isCancelScheduled}
              aria-disabled={isCancelScheduled}
            >
              Cancel Subscription
            </button>
            {isCancelScheduled
              ? <p>Your subscription is already cancelled and stays active until {cancelScheduledUntil}.</p>
              : <p>Your Plan will remain active until {activeUntil || '—'}</p>}
          </footer>
        )}
      </div>

      <CancelSubscriptionModal
        isOpen={showCancel && !isCancelScheduled}
        onClose={() => setShowCancel(false)}
        onConfirm={handleCancelConfirmed}
        loading={cancelLoading}
      />

      {/* Cancel result — SAME <PopUp> (checkmark/failed icon + Close) the
          More-menu shows, so the two flows look identical. */}
      {message && (
        <PopUp
          type={popupType}
          message={message}
          onClose={() => setMessage('')}
        />
      )}

      {/* "You're on the highest plan" — same message as RecommendedPlan. */}
      {showTopTier && (
        <div className={styles.topTierBackdrop} role="dialog" aria-modal="true">
          <div className={styles.topTierCard}>
            <h3>You're on the highest plan</h3>
            <p>
              No higher tier is available right now. Need a custom setup? Contact{' '}
              <a href="mailto:support@rxpt.us">support@rxpt.us</a>.
            </p>
            <button type="button" onClick={() => setShowTopTier(false)}>Got it</button>
          </div>
        </div>
      )}
    </main>
  )
}

export default ManageSubscription
